import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, Calendar, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { API, authHeaders } from "./shared";

/**
 * Modal "Storia Snapshot Magazzino" — vista tabellare degli snapshot
 * giornalieri di un cliente per un mese o un intervallo di date.
 *
 * Props:
 *   client: { client_email, client_name }
 *   defaultMonth: "YYYY-MM"
 *   onClose: () => void
 */
export default function SnapshotHistoryModal({ client, defaultMonth, onClose }) {
  const [mode, setMode] = useState("month"); // "month" | "range"
  const [month, setMonth] = useState(defaultMonth);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ client_email: client.client_email });
      if (mode === "range" && dateFrom && dateTo) {
        params.set("date_from", dateFrom);
        params.set("date_to", dateTo);
      } else {
        params.set("month", month);
      }
      const r = await axios.get(`${API}/admin/warehouse/snapshots?${params}`, authHeaders());
      setSnapshots(r.data.snapshots || []);
    } catch {
      toast.error("Errore nel caricamento dello storico");
    } finally {
      setLoading(false);
    }
  }, [client.client_email, mode, month, dateFrom, dateTo]);

  useEffect(() => {
    if (mode === "month" || (mode === "range" && dateFrom && dateTo)) {
      load();
    }
  }, [load, mode, dateFrom, dateTo]);

  // Calcolo variazione giornaliera e statistiche
  const rows = snapshots.map((s, i) => {
    const prev = i > 0 ? snapshots[i - 1] : null;
    const delta = prev != null ? (s.pallets || 0) - (prev.pallets || 0) : null;
    return { ...s, delta };
  });

  const totalDays = rows.length;
  const avgPallets = totalDays > 0 ? rows.reduce((sum, r) => sum + (r.pallets || 0), 0) / totalDays : 0;
  const maxPallets = totalDays > 0 ? Math.max(...rows.map((r) => r.pallets || 0)) : 0;
  const minPallets = totalDays > 0 ? Math.min(...rows.map((r) => r.pallets || 0)) : 0;

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="snapshot-history-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <h3 className="text-lg font-bold text-[#09090B]">Storia Snapshot Magazzino</h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              {client.client_name}
              <span className="text-zinc-300 mx-1.5">·</span>
              <span className="text-zinc-400">{client.client_email}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 transition-colors"
            data-testid="snapshot-history-close"
            aria-label="Chiudi"
          >
            <X size={20} />
          </button>
        </div>

        {/* Controls */}
        <div className="px-5 py-3 border-b border-zinc-100 bg-zinc-50 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-sm overflow-hidden border border-zinc-300">
            <button
              onClick={() => setMode("month")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${mode === "month" ? "bg-[#09090B] text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"}`}
              data-testid="snapshot-mode-month"
            >
              Mese
            </button>
            <button
              onClick={() => setMode("range")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${mode === "range" ? "bg-[#09090B] text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"}`}
              data-testid="snapshot-mode-range"
            >
              Intervallo
            </button>
          </div>

          {mode === "month" ? (
            <div className="flex items-center gap-2 bg-white border border-zinc-300 rounded-sm px-3 py-1.5">
              <Calendar size={14} className="text-zinc-500" />
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="text-sm outline-none"
                data-testid="snapshot-history-month"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 bg-white border border-zinc-300 rounded-sm px-2 py-1.5">
                <span className="text-[10px] uppercase text-zinc-500 font-bold">Da</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="text-sm outline-none"
                  data-testid="snapshot-history-from"
                />
              </div>
              <div className="flex items-center gap-1 bg-white border border-zinc-300 rounded-sm px-2 py-1.5">
                <span className="text-[10px] uppercase text-zinc-500 font-bold">A</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="text-sm outline-none"
                  data-testid="snapshot-history-to"
                />
              </div>
            </div>
          )}

          {totalDays > 0 && (
            <div className="ml-auto flex items-center gap-4 text-xs">
              <div>
                <div className="text-[10px] uppercase text-zinc-400 font-bold tracking-widest">Media</div>
                <div className="font-bold text-[#09090B]" data-testid="snapshot-stats-avg">{avgPallets.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-400 font-bold tracking-widest">Min</div>
                <div className="font-bold text-[#09090B]">{minPallets.toFixed(0)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-400 font-bold tracking-widest">Max</div>
                <div className="font-bold text-[#09090B]">{maxPallets.toFixed(0)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-400 font-bold tracking-widest">Giorni</div>
                <div className="font-bold text-[#09090B]">{totalDays}</div>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-zinc-500">
              <Loader2 size={20} className="animate-spin inline mr-2" />
              Caricamento snapshot...
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-zinc-500" data-testid="snapshot-history-empty">
              Nessuno snapshot registrato per il periodo selezionato.
              <br />
              <span className="text-xs text-zinc-400">Usa "Backfill Mese" per ricostruire lo storico dai movimenti.</span>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
                <tr>
                  <th className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Data</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Bancali</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Variazione</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Origine</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.date} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`snapshot-row-${r.date}`}>
                    <td className="px-5 py-2 text-[#09090B]">
                      <div className="font-medium">{formatDate(r.date)}</div>
                      <div className="text-[10px] text-zinc-400">{r.date}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-[#09090B]">
                      {(r.pallets || 0).toFixed(r.pallets % 1 === 0 ? 0 : 2)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.delta === null ? (
                        <span className="text-zinc-300">—</span>
                      ) : r.delta > 0 ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-bold">
                          <TrendingUp size={12} /> +{r.delta.toFixed(r.delta % 1 === 0 ? 0 : 2)}
                        </span>
                      ) : r.delta < 0 ? (
                        <span className="inline-flex items-center gap-1 text-red-600 font-bold">
                          <TrendingDown size={12} /> {r.delta.toFixed(r.delta % 1 === 0 ? 0 : 2)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-zinc-400">
                          <Minus size={12} /> 0
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] uppercase text-zinc-400 tracking-wider">
                      {r.source === "backfill" ? "backfill" : "cron"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
